import { useState } from 'react';
import { User } from '../../../@types/users';
import { ActionButton } from '../../../components/action-button';
import { CheckboxInput } from '../../../components/checkbox-input';
import { Modal } from '../../../components/modal';
import { PERMISSION } from '../../../consts/permissions';
import { useAuthPermissions } from '../../../hooks/auth';
import { useCreateUser } from '../../../queries/users';
import {
  ButtonsRow,
  CheckboxRow,
  CustomInput,
  CustomSelect,
  ErrorMessage,
  OptionalFieldsContainer,
  OptionalToggle,
  SelectLabel,
  SelectWrapper,
} from './style';

const SYSTEM_ROLES = [
  { id: 5, name: 'מתנדב' },
  { id: 4, name: 'אחראי חלוקה' },
  { id: 3, name: 'אחראי כתובות' },
  { id: 2, name: "צ'יף" },
];

type AddUserModalProps = {
  initialFirstName?: string;
  initialLastName?: string;
  initialIsDriver?: boolean;
  onUserCreated?: (user: User) => void;
  onCancel: () => void;
};

export const AddUserModal = ({
  initialFirstName = '',
  initialLastName = '',
  initialIsDriver = false,
  onUserCreated,
  onCancel,
}: AddUserModalProps) => {
  const [firstName, setFirstName] = useState(initialFirstName);
  const [lastName, setLastName] = useState(initialLastName);
  const [isDriver, setIsDriver] = useState(initialIsDriver);
  const [systemRoleId, setSystemRoleId] = useState<number>(5);

  const { hasPermissions } = useAuthPermissions();
  const canSetRole = hasPermissions([PERMISSION.SET_USER_ROLE]);
  const canSetUser = hasPermissions([PERMISSION.SET_USER_CREDENTIALS]);

  const [showOptionalCredentials, setShowOptionalCredentials] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');

  const [errorMessage, setErrorMessage] = useState('');

  const { mutate: mutateCreateUser, isLoading: isCreating } = useCreateUser();

  const handleSave = () => {
    setErrorMessage('');

    const trimmedFirstName = firstName.trim();
    const trimmedLastName = lastName.trim();
    const trimmedUsername = username.trim();
    const trimmedPassword = password.trim();

    if (!trimmedFirstName) {
      setErrorMessage('יש להזין שם פרטי');

      return;
    }

    if (!trimmedLastName) {
      setErrorMessage('יש להזין שם משפחה');

      return;
    }

    if (canSetUser && trimmedUsername && !trimmedPassword) {
      setErrorMessage('יש להזין סיסמה');

      return;
    }

    if (canSetUser && trimmedPassword && !trimmedUsername) {
      setErrorMessage('יש להזין שם משתמש');

      return;
    }

    mutateCreateUser(
      {
        firstName: trimmedFirstName,
        lastName: trimmedLastName,
        isDriver,
        systemRoleId: canSetRole ? systemRoleId : undefined,
        username: canSetUser ? trimmedUsername || undefined : undefined,
        password: canSetUser ? trimmedPassword || undefined : undefined,
      },
      {
        onSuccess: (createdUser) => {
          if (onUserCreated) {
            onUserCreated(createdUser);
          }
          onCancel();
        },
        onError: (error) => {
          const serverMessage = error.response?.data?.errorMessage;
          setErrorMessage(serverMessage || 'אירעה שגיאה ביצירת המשתמש');
        },
      },
    );
  };

  return (
    <Modal title="הוספת משתמש חדש" isVisible onClose={onCancel}>
      <form
        onSubmit={(event) => {
          event.stopPropagation();
          event.preventDefault();
          handleSave();
        }}
      >
        <CustomInput
          label="שם פרטי"
          value={firstName}
          onChange={(e) => setFirstName(e.currentTarget.value)}
          placeholder="לדוגמה: ישראל"
          autoFocus
        />

        <CustomInput
          label="שם משפחה"
          value={lastName}
          onChange={(e) => setLastName(e.currentTarget.value)}
          placeholder="לדוגמה: ישראלי"
        />

        <CheckboxRow>
          <CheckboxInput
            label="נהג"
            isChecked={isDriver}
            onToggleCheck={() => setIsDriver(!isDriver)}
          />
        </CheckboxRow>

        {canSetRole && (
          <SelectWrapper>
            <SelectLabel>תפקיד</SelectLabel>
            <CustomSelect
              value={systemRoleId}
              onChange={(e) => setSystemRoleId(Number(e.currentTarget.value))}
            >
              {SYSTEM_ROLES.map((role) => (
                <option key={role.id} value={role.id}>
                  {role.name}
                </option>
              ))}
            </CustomSelect>
          </SelectWrapper>
        )}

        {canSetUser && (
          <div>
            <OptionalToggle
              type="button"
              onClick={() => setShowOptionalCredentials(!showOptionalCredentials)}
            >
              {showOptionalCredentials
                ? '- הסתרת פרטי התחברות (אופציונלי)'
                : '+ הוספת פרטי התחברות (אופציונלי)'}
            </OptionalToggle>

            {showOptionalCredentials && (
              <OptionalFieldsContainer>
                <CustomInput
                  label="שם משתמש"
                  value={username}
                  onChange={(e) => setUsername(e.currentTarget.value)}
                  placeholder="למשתמש בעל גישה למערכת"
                />
                <CustomInput
                  label="סיסמה"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.currentTarget.value)}
                />
              </OptionalFieldsContainer>
            )}
          </div>
        )}

        {!!errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}

        <ButtonsRow>
          <ActionButton
            title={isCreating ? 'שומר...' : 'שמירה'}
            type="submit"
            disabled={isCreating}
          />
          <ActionButton title="ביטול" onClick={onCancel} kind="secondary" disabled={isCreating} />
        </ButtonsRow>
      </form>
    </Modal>
  );
};
